import { REGISTRATION_OPENS_AT } from "../config/event";

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const STORAGE_KEY = "mystery.accessCode";
const VERIFY_ENDPOINT = "/backend/verify_access_code.php";

// Fallback when sessionStorage is blocked, so the code survives in-app navigation.
let memoryCode = "";

/**
 * Splits the time left until `opensAt` into countdown units.
 * @param {number} now epoch milliseconds
 * @param {string} [opensAt] ISO 8601 timestamp
 * @returns {{ days: number, hours: number, minutes: number, seconds: number, isOver: boolean }}
 */
export const getTimeLeft = (now, opensAt = REGISTRATION_OPENS_AT) => {
  const remaining = Math.max(0, new Date(opensAt).getTime() - now);
  return {
    days: Math.floor(remaining / DAY_MS),
    hours: Math.floor((remaining % DAY_MS) / HOUR_MS),
    minutes: Math.floor((remaining % HOUR_MS) / MINUTE_MS),
    seconds: Math.floor((remaining % MINUTE_MS) / SECOND_MS),
    isOver: remaining === 0,
  };
};

/** @returns {string} the verified early-access code for this tab session, or "" */
export const getStoredAccessCode = () => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    return memoryCode;
  }
};

/** @param {string} code a code the backend has already accepted */
export const storeAccessCode = (code) => {
  memoryCode = code;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Storage blocked (e.g. strict privacy mode): memoryCode keeps it for this page load.
  }
};

/**
 * Asks the backend whether `code` unlocks early registration.
 * @param {string} code
 * @returns {Promise<boolean>}
 * @throws {Error} when the backend is unreachable or answers with an error status
 */
export const verifyAccessCode = async (code) => {
  const response = await fetch(VERIFY_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) {
    throw new Error(`Access code check failed with status ${response.status}`);
  }
  const data = await response.json();
  return data.valid === true;
};
